import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    Autocomplete,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Drawer,
    FormControlLabel,
    Link,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    AccountTreeRounded,
    AddRounded,
    DeleteOutlineRounded,
    TableRowsRounded,
    ViewKanbanRounded,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import ObjectIcon from "../components/ObjectIcon";
import { Button } from "../components/Buttons";
import { useObjects } from "../context/ObjectsContext";
import {
    createRecord,
    deleteRecord,
    getAssignableUsers,
    getRecordTransitions,
    getRelatedRecords,
    listPipelines,
    listRecords,
    transitionRecord,
    updateRecord,
} from "../api";
import { formatDate } from "../utils/format";
import { apiError, formatFieldValue, userName } from "../utils/meta";

/** Small-caps section label, as used by the drawer header and the builders. */
const SECTION_LABEL_SX = {
    fontSize: 11,
    letterSpacing: "2px",
    fontWeight: 700,
    color: "var(--text-tertiary)",
};

/* A terminal stage should read differently at a glance: won leans on the
   accent, lost recedes into the muted greys. */
const STAGE_TONES = {
    open: {
        background: "var(--bg-secondary)",
        borderColor: "var(--border-mid)",
        color: "var(--text)",
    },
    won: {
        background: "var(--accent-soft)",
        borderColor: "var(--accent)",
        color: "var(--accent)",
    },
    lost: {
        background: "var(--bg-tint)",
        borderColor: "var(--border-mid)",
        color: "var(--text-tertiary)",
    },
};

/* ------------------------------------------------------------------ */
/* Stage chip — colour dot + stage name                                 */
/* ------------------------------------------------------------------ */

export function StageChip({ name, color, kind, large = false }) {
    const tone = STAGE_TONES[kind] ?? STAGE_TONES.open;
    return (
        <Stack
            component="span"
            sx={{
                flexDirection: "row",
                alignItems: "center",
                gap: 0.875,
                width: "fit-content",
                border: "1px solid",
                borderRadius: "3px",
                px: large ? 1.25 : 1,
                py: large ? 0.625 : 0.375,
                ...tone,
            }}
        >
            <Stack
                component="span"
                sx={{
                    width: large ? 9 : 8,
                    height: large ? 9 : 8,
                    flexShrink: 0,
                    borderRadius: "2px",
                    background: color || "var(--border-strong)",
                }}
            />
            <Typography
                component="span"
                sx={{
                    fontSize: large ? 13.5 : 12.5,
                    fontWeight: 600,
                    letterSpacing: "0.2px",
                    color: "inherit",
                }}
            >
                {name || "No stage"}
            </Typography>
        </Stack>
    );
}

/* ------------------------------------------------------------------ */
/* One input, rendered from field metadata                              */
/* ------------------------------------------------------------------ */

/** The best human label for a record of another object. */
export const recordLabel = (record, displayField) => {
    const data = record?.data ?? {};
    if (displayField && data[displayField]) return String(data[displayField]);
    const first = Object.values(data).find(
        (v) => v !== null && v !== undefined && String(v).trim() !== "",
    );
    return first ? String(first) : "Untitled";
};

/**
 * Picker for a `relation` field: searches the linked object's records and
 * stores the chosen record's id. `initialLabel` comes from the record's
 * relationLabels, so an existing link shows a name instead of a UUID.
 */
function RelationInput({ field, value, onChange, initialLabel }) {
    const { objectSlug, objectName, displayField } = field.config ?? {};
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    /* Fetch on open and as the user types (debounced). */
    useEffect(() => {
        if (!open || !objectSlug) return undefined;
        let alive = true;
        const timer = setTimeout(async () => {
            setLoading(true);
            try {
                const { data } = await listRecords(objectSlug, {
                    search: search || undefined,
                    limit: 20,
                });
                if (alive) setOptions(data?.data ?? []);
            } catch (error) {
                console.error("Failed to load linked records", { error });
                if (alive) setOptions([]);
            } finally {
                if (alive) setLoading(false);
            }
        }, search ? 250 : 0);
        return () => {
            alive = false;
            clearTimeout(timer);
        };
    }, [open, search, objectSlug]);

    /* The selected record may not be in the current page of options, so keep
       its label separately rather than depending on the list. */
    const selected = options.find((o) => o.id === value) ?? null;
    const selectedLabel = selected
        ? recordLabel(selected, displayField)
        : value
          ? initialLabel || `#${String(value).slice(0, 8)}`
          : "";

    return (
        <Autocomplete
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            options={options}
            loading={loading}
            filterOptions={(x) => x}
            getOptionLabel={(o) =>
                typeof o === "string" ? o : recordLabel(o, displayField)
            }
            isOptionEqualToValue={(o, v) => o.id === (v?.id ?? v)}
            value={selected ?? (value ? { id: value } : null)}
            onInputChange={(_e, text, reason) => {
                if (reason === "input") setSearch(text);
            }}
            onChange={(_e, picked) => onChange(picked ? picked.id : "")}
            noOptionsText={
                loading ? "Searching…" : `No ${objectName || "records"} yet`
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={field.label}
                    required={!!field.required}
                    placeholder={
                        value ? selectedLabel : `Search ${objectName || "records"}…`
                    }
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading && <CircularProgress size={15} />}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}

export function DynamicFieldInput({
    field,
    value,
    onChange,
    users = [],
    relationLabels = {},
}) {
    const common = {
        label: field.label,
        required: !!field.required,
        fullWidth: true,
        value: value ?? "",
        onChange: (e) => onChange(e.target.value),
    };

    switch (field.type) {
        case "textarea":
            return <TextField {...common} multiline minRows={3} />;
        case "number":
        case "currency":
            return <TextField {...common} type="number" />;
        case "date":
            return (
                <TextField
                    {...common}
                    type="date"
                    InputLabelProps={{ shrink: true }}
                />
            );
        case "datetime":
            return (
                <TextField
                    {...common}
                    type="datetime-local"
                    InputLabelProps={{ shrink: true }}
                />
            );
        case "boolean":
            return (
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={value === true || value === "true"}
                            onChange={(e) => onChange(e.target.checked)}
                            sx={{
                                color: "var(--border-strong)",
                                "&.Mui-checked": { color: "var(--accent)" },
                            }}
                        />
                    }
                    label={field.label}
                />
            );
        case "email":
            return <TextField {...common} type="email" />;
        case "phone":
            return <TextField {...common} type="tel" />;
        case "url":
            return <TextField {...common} type="url" />;
        case "picklist":
            return (
                <TextField {...common} select>
                    {(field.config?.options ?? []).map((opt) => (
                        <MenuItem key={opt} value={opt}>
                            {opt}
                        </MenuItem>
                    ))}
                </TextField>
            );
        case "user":
            return (
                <TextField {...common} select>
                    {users.map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                            {userName(u)}
                        </MenuItem>
                    ))}
                </TextField>
            );
        case "relation":
            return (
                <RelationInput
                    field={field}
                    value={value}
                    onChange={onChange}
                    initialLabel={relationLabels[field.key]}
                />
            );
        default:
            return <TextField {...common} />;
    }
}

/* ------------------------------------------------------------------ */
/* Linked records — every object that points at this one                 */
/* ------------------------------------------------------------------ */

/**
 * Reads the relation graph backwards: any object with a relation field
 * targeting this one shows up here as a group. "Add <Object>" carries the
 * parent id across so the child opens with its link prefilled — that's how a
 * tenant walks Lead → Journey → Opportunity.
 */
function LinkedRecords({ object, recordId }) {
    const navigate = useNavigate();
    const [groups, setGroups] = useState(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await getRelatedRecords(object.slug, recordId);
                if (alive) setGroups(data?.groups ?? []);
            } catch (error) {
                console.error("Failed to load linked records", { error });
                if (alive) setGroups([]);
            }
        })();
        return () => {
            alive = false;
        };
    }, [object.slug, recordId]);

    if (!groups || !groups.length) return null;

    return (
        <Stack sx={{ gap: 1.5 }}>
            <Typography sx={SECTION_LABEL_SX}>LINKED RECORDS</Typography>

            {groups.map((group) => (
                <Stack
                    key={group.object.id}
                    sx={{
                        border: "1px solid var(--border-mid)",
                        borderRadius: "4px",
                        background: "var(--bg-secondary)",
                    }}
                >
                    <Stack
                        sx={{
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                            p: "12px 14px",
                            borderBottom: group.records.length
                                ? "1px solid var(--border)"
                                : "none",
                        }}
                    >
                        <Stack
                            sx={{
                                flexDirection: "row",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <ObjectIcon name={group.object.icon} size={16} />
                            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                                {group.object.name}
                            </Typography>
                            <Typography
                                sx={{ fontSize: 12.5, color: "var(--text-tertiary)" }}
                            >
                                {group.records.length}
                            </Typography>
                        </Stack>

                        {group.canCreate && (
                            <Button
                                variant="text"
                                startIcon={<AddRounded />}
                                sx={{ height: 30, px: 1 }}
                                onClick={() =>
                                    navigate(`/o/${group.object.slug}`, {
                                        state: {
                                            openCreate: true,
                                            prefill: {
                                                [group.viaField.key]: recordId,
                                            },
                                        },
                                    })
                                }
                            >
                                Add {group.object.name}
                            </Button>
                        )}
                    </Stack>

                    {group.records.length === 0 ? (
                        <Typography
                            sx={{
                                fontSize: 13,
                                color: "var(--text-tertiary)",
                                p: "12px 14px",
                            }}
                        >
                            No {group.object.name} yet
                        </Typography>
                    ) : (
                        group.records.map((r) => (
                            <Stack
                                key={r.id}
                                onClick={() => navigate(`/o/${group.object.slug}`)}
                                sx={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1,
                                    p: "10px 14px",
                                    cursor: "pointer",
                                    "&:not(:last-of-type)": {
                                        borderBottom: "1px solid var(--border)",
                                    },
                                    "&:hover": { background: "var(--bg-tint-soft)" },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 13.5,
                                        fontWeight: 500,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {recordLabel(r)}
                                </Typography>
                                <Stack
                                    sx={{
                                        flexDirection: "row",
                                        alignItems: "center",
                                        gap: 1,
                                        flexShrink: 0,
                                    }}
                                >
                                    {r.stageName && (
                                        <StageChip
                                            name={r.stageName}
                                            color={r.stageColor}
                                            kind={r.stageKind}
                                        />
                                    )}
                                    <Typography
                                        sx={{
                                            fontSize: 12.5,
                                            color: "var(--text-tertiary)",
                                        }}
                                    >
                                        {r.ownerName || "—"}
                                    </Typography>
                                </Stack>
                            </Stack>
                        ))
                    )}
                </Stack>
            ))}
        </Stack>
    );
}

/* ------------------------------------------------------------------ */
/* Stage control — current stage plus the moves this caller may make     */
/* ------------------------------------------------------------------ */

function StageSection({ object, record, onRecordChange, onRefresh }) {
    const navigate = useNavigate();
    const [transitions, setTransitions] = useState(null);
    const [loading, setLoading] = useState(true);
    const [movingTo, setMovingTo] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await getRecordTransitions(object.slug, record.id);
            setTransitions(data ?? null);
        } catch (error) {
            console.error("Failed to load stage transitions", { error });
            setTransitions(null);
        } finally {
            setLoading(false);
        }
    }, [object.slug, record.id]);

    useEffect(() => {
        load();
    }, [load]);

    /* On success and on failure alike, re-ask the API: a move changes which
       moves are legal next, and a rejection means our view was stale. */
    async function handleMove(option) {
        setMovingTo(option.stageId);
        try {
            const { data } = await transitionRecord(
                object.slug,
                record.id,
                option.stageId,
            );
            toast.success(`Moved to ${option.name}`);
            onRecordChange(data);
            onRefresh();
        } catch (err) {
            toast.error(apiError(err, "That move isn't allowed"));
        } finally {
            setMovingTo(null);
            load();
        }
    }

    const options = transitions?.options ?? [];
    const blocked = options.filter((o) => !o.allowed);
    const current = transitions?.currentStage ?? null;
    const busy = movingTo !== null;

    return (
        <Stack
            sx={{
                gap: 1.5,
                pb: 2.5,
                borderBottom: "1px solid var(--border)",
            }}
        >
            <Stack
                sx={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                }}
            >
                <Typography sx={SECTION_LABEL_SX}>STAGE</Typography>
                <Link
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={() => navigate(`/o/${object.slug}/board`)}
                    sx={{
                        fontFamily: "inherit",
                        fontSize: 12.5,
                        fontWeight: 600,
                        letterSpacing: "0.2px",
                        color: "var(--text-secondary)",
                        background: "none",
                        border: "none",
                        p: 0,
                        cursor: "pointer",
                        "&:hover": { color: "var(--text)" },
                    }}
                >
                    Open board
                </Link>
            </Stack>

            {loading && !transitions ? (
                <CircularProgress size={20} />
            ) : (
                <StageChip
                    large
                    name={current?.name ?? record.stageName}
                    color={current?.color ?? record.stageColor}
                    kind={current?.kind ?? record.stageKind}
                />
            )}

            {options.length > 0 && (
                <Stack
                    sx={{ flexDirection: "row", flexWrap: "wrap", gap: 1 }}
                >
                    {options.map((opt) => {
                        const label = `Move to ${opt.name}`;
                        /* Button paints its colours through `sx`, so a
                           disabled option needs its own muted override. */
                        const sx = {
                            height: 36,
                            px: 1.75,
                            fontSize: 13,
                            fontWeight: 600,
                            "&.Mui-disabled": {
                                background: "transparent",
                                borderColor: "var(--border)",
                                color: "var(--text-tertiary)",
                            },
                        };
                        if (!opt.allowed) {
                            return (
                                <Tooltip
                                    key={opt.stageId}
                                    arrow
                                    title={
                                        opt.reason ??
                                        "This move isn't available"
                                    }
                                >
                                    <span>
                                        <Button
                                            variant="soft-outlined"
                                            disabled
                                            sx={sx}
                                        >
                                            {label}
                                        </Button>
                                    </span>
                                </Tooltip>
                            );
                        }
                        return (
                            <Button
                                key={opt.stageId}
                                variant="soft-outlined"
                                loading={movingTo === opt.stageId}
                                disabled={busy}
                                sx={sx}
                                onClick={() => handleMove(opt)}
                            >
                                {label}
                            </Button>
                        );
                    })}
                </Stack>
            )}

            {/* The reasons again, in the open — a guard the tenant wrote is
                only useful if they can read it without hunting for a hover. */}
            {blocked.length > 0 && (
                <Stack sx={{ gap: 0.5 }}>
                    {blocked.map((opt) => (
                        <Typography
                            key={opt.stageId}
                            sx={{
                                fontSize: 12,
                                lineHeight: 1.55,
                                color: "var(--text-tertiary)",
                            }}
                        >
                            {opt.name} is blocked —{" "}
                            {opt.reason ?? "this move isn't available"}
                        </Typography>
                    ))}
                </Stack>
            )}

            {!loading && transitions && options.length === 0 && (
                <Typography
                    sx={{ fontSize: 12.5, color: "var(--text-tertiary)" }}
                >
                    {transitions?.pipeline
                        ? "No moves lead out of this stage. Add a transition in the pipeline builder."
                        : "This record isn't on a pipeline yet."}
                </Typography>
            )}
        </Stack>
    );
}

/* ------------------------------------------------------------------ */
/* Create / edit drawer                                                 */
/* ------------------------------------------------------------------ */

function RecordEditor({
    open,
    object,
    record,
    prefill,
    users,
    pipeline,
    onClose,
    onSaved,
    onRefresh,
}) {
    const [data, setData] = useState({});
    const [ownerId, setOwnerId] = useState("");
    const [stageId, setStageId] = useState("");
    const [live, setLive] = useState(null);
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleteBusy, setDeleteBusy] = useState(false);

    const fields = useMemo(
        () =>
            [...(object?.fields ?? [])].sort(
                (a, b) => (a.position ?? 0) - (b.position ?? 0),
            ),
        [object],
    );

    const stages = useMemo(
        () =>
            [...(pipeline?.stages ?? [])].sort(
                (a, b) => (a.position ?? 0) - (b.position ?? 0),
            ),
        [pipeline],
    );

    useEffect(() => {
        if (!open) return;
        /* A create opened from a parent's linked records arrives with that
           relation already set. */
        setData(record?.data ? { ...record.data } : { ...(prefill ?? {}) });
        setOwnerId(record?.ownerId ?? "");
        setStageId(stages[0]?.id ?? "");
        setLive(record ?? null);
        setConfirmDelete(false);
    }, [open, record, prefill, stages]);

    async function handleSave() {
        const missing = fields.filter((f) => {
            if (!f.required) return false;
            const v = data[f.key];
            if (f.type === "boolean") return false;
            return v === undefined || v === null || v === "";
        });
        if (missing.length) {
            toast.error(
                `Required: ${missing.map((f) => f.label).join(", ")}`,
            );
            return;
        }
        setSaving(true);
        try {
            const body = { data };
            if (ownerId) body.ownerId = ownerId;
            if (record?.id) {
                await updateRecord(object.slug, record.id, body);
                toast.success("Record updated");
            } else {
                if (stageId) body.stageId = stageId;
                await createRecord(object.slug, body);
                toast.success(`${object.name} record created`);
            }
            onSaved();
        } catch (err) {
            toast.error(apiError(err, "Could not save the record"));
        } finally {
            setSaving(false);
        }
    }

    async function handleDelete() {
        setDeleteBusy(true);
        try {
            await deleteRecord(object.slug, record.id);
            toast.success("Record deleted");
            setConfirmDelete(false);
            onSaved();
        } catch (err) {
            toast.error(apiError(err, "Could not delete the record"));
        } finally {
            setDeleteBusy(false);
        }
    }

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: { width: { xs: "100%", md: 520 }, background: "var(--bg)" },
            }}
        >
            <Stack sx={{ height: "100%" }}>
                <Stack
                    sx={{
                        p: "24px 28px 20px",
                        borderBottom: "1px solid var(--border)",
                        gap: 0.5,
                        background: "var(--bg-secondary)",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 11,
                            letterSpacing: "2px",
                            fontWeight: 700,
                            color: "var(--text-tertiary)",
                        }}
                    >
                        {object?.name?.toUpperCase()}
                    </Typography>
                    <Typography variant="h5">
                        {record ? "Edit record" : `New ${object?.name ?? ""}`}
                    </Typography>
                </Stack>

                <Stack
                    sx={{ flex: 1, overflowY: "auto", p: "24px 28px", gap: 2.5 }}
                >
                    {live?.id && (pipeline || live.stageId) && (
                        <StageSection
                            object={object}
                            record={live}
                            onRecordChange={setLive}
                            onRefresh={onRefresh}
                        />
                    )}

                    {!record && stages.length > 0 && (
                        <TextField
                            label="Starting stage"
                            select
                            fullWidth
                            value={stageId}
                            onChange={(e) => setStageId(e.target.value)}
                        >
                            {stages.map((s) => (
                                <MenuItem key={s.id} value={s.id}>
                                    {s.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}

                    {fields.map((f) => (
                        <DynamicFieldInput
                            key={f.id}
                            field={f}
                            value={data[f.key]}
                            users={users}
                            relationLabels={live?.relationLabels ?? {}}
                            onChange={(v) =>
                                setData((prev) => ({ ...prev, [f.key]: v }))
                            }
                        />
                    ))}

                    <TextField
                        label="Owner"
                        select
                        fullWidth
                        value={ownerId}
                        onChange={(e) => setOwnerId(e.target.value)}
                    >
                        {users.map((u) => (
                            <MenuItem key={u.id} value={u.id}>
                                {userName(u)}
                            </MenuItem>
                        ))}
                    </TextField>

                    {live?.id && <LinkedRecords object={object} recordId={live.id} />}
                </Stack>

                <Stack
                    sx={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: "16px 28px",
                        borderTop: "1px solid var(--border)",
                        background: "var(--bg-secondary)",
                    }}
                >
                    {record ? (
                        <Button
                            variant="text"
                            color="error"
                            startIcon={<DeleteOutlineRounded />}
                            onClick={() => setConfirmDelete(true)}
                        >
                            Delete
                        </Button>
                    ) : (
                        <span />
                    )}
                    <Stack sx={{ flexDirection: "row", gap: 1.5 }}>
                        <Button variant="soft-outlined" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            loading={saving}
                            onClick={handleSave}
                        >
                            {record ? "Save changes" : "Create"}
                        </Button>
                    </Stack>
                </Stack>
            </Stack>

            <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
                <DialogTitle
                    sx={{ fontFamily: "'Newsreader', Georgia, serif" }}
                >
                    Delete this record?
                </DialogTitle>
                <DialogContent>
                    <Typography
                        sx={{ fontSize: 14.5, color: "var(--text-secondary)" }}
                    >
                        This record will be removed permanently.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button
                        variant="soft-outlined"
                        onClick={() => setConfirmDelete(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        loading={deleteBusy}
                        onClick={handleDelete}
                    >
                        Delete record
                    </Button>
                </DialogActions>
            </Dialog>
        </Drawer>
    );
}

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */

export default function ObjectRecords() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { objects, loading: objectsLoading } = useObjects();
    const object = objects.find((o) => o.slug === slug) ?? null;

    const [rows, setRows] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 25,
        total: 0,
    });
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [pipeline, setPipeline] = useState(null);
    const [editor, setEditor] = useState({
        open: false,
        record: null,
        prefill: null,
    });

    /* "Add <Object>" from a parent record navigates here with the link to
       prefill; consume it once so a later back-navigation doesn't reopen. */
    useEffect(() => {
        const state = location.state;
        if (!state?.openCreate) return;
        setEditor({ open: true, record: null, prefill: state.prefill ?? null });
        navigate(location.pathname, { replace: true, state: null });
    }, [location.state, location.pathname, navigate]);

    const load = useCallback(
        async (page = 1, limit = 25, searchTerm = "") => {
            setLoading(true);
            try {
                const { data } = await listRecords(slug, {
                    page,
                    limit,
                    search: searchTerm,
                });
                setRows(data.data ?? []);
                setPagination(data.pagination ?? { page, limit, total: 0 });
            } catch (err) {
                toast.error(apiError(err, "Failed to load records"));
            } finally {
                setLoading(false);
            }
        },
        [slug],
    );

    /* Debounce the search box so typing doesn't hammer the API. */
    useEffect(() => {
        const t = setTimeout(
            () => load(1, pagination.limit, search),
            search ? 350 : 0,
        );
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, load]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await getAssignableUsers();
                setUsers(Array.isArray(data) ? data : (data?.data ?? []));
            } catch (error) {
                console.error("Failed to load assignable users", { error });
            }
        })();
    }, []);

    /* The object's pipeline, fetched once: it tells us whether to show the
       stage column and which stages a new record may start in. */
    useEffect(() => {
        if (!object?.id) return;
        let stale = false;
        (async () => {
            try {
                const { data } = await listPipelines(object.id);
                const list = Array.isArray(data) ? data : [];
                if (!stale) {
                    setPipeline(
                        list.find((p) => p.isDefault) ?? list[0] ?? null,
                    );
                }
            } catch (error) {
                console.error("Failed to load pipelines", { error });
            }
        })();
        return () => {
            stale = true;
        };
    }, [object?.id]);

    const userById = useMemo(() => {
        const map = {};
        users.forEach((u) => {
            map[u.id] = userName(u);
        });
        return map;
    }, [users]);

    const showStage = !!pipeline || rows.some((r) => r.stageId);

    const columns = useMemo(() => {
        const fields = [...(object?.fields ?? [])]
            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
            .slice(0, 5);
        return [
            ...fields.map((f) => ({
                key: f.key,
                label: f.label,
                render: (row) =>
                    f.type === "user"
                        ? (userById[row.data?.[f.key]] ??
                          formatFieldValue(f, row.data?.[f.key]))
                        : formatFieldValue(f, row.data?.[f.key]),
            })),
            ...(showStage
                ? [
                      {
                          key: "stageName",
                          label: "Stage",
                          render: (row) =>
                              row.stageId ? (
                                  <StageChip
                                      name={row.stageName}
                                      color={row.stageColor}
                                      kind={row.stageKind}
                                  />
                              ) : (
                                  <Typography
                                      sx={{
                                          fontSize: 13,
                                          color: "var(--text-tertiary)",
                                      }}
                                  >
                                      —
                                  </Typography>
                              ),
                      },
                  ]
                : []),
            {
                key: "ownerName",
                label: "Owner",
                render: (row) =>
                    row.ownerName ?? userById[row.ownerId] ?? "—",
            },
            {
                key: "createdAt",
                label: "Created",
                render: (row) => formatDate(row.createdAt),
            },
        ];
    }, [object, userById, showStage]);

    if (objectsLoading) {
        return (
            <Stack sx={{ alignItems: "center", py: 12 }}>
                <CircularProgress size={28} />
            </Stack>
        );
    }

    if (!object) {
        return (
            <Stack sx={{ alignItems: "center", py: 12, gap: 2 }}>
                <Typography variant="h6">Object not found</Typography>
                <Typography
                    sx={{ fontSize: 14, color: "var(--text-secondary)" }}
                >
                    No object with slug &#8220;{slug}&#8221; exists in this
                    workspace.
                </Typography>
                <Button
                    variant="soft-outlined"
                    onClick={() => navigate("/admin/objects")}
                >
                    Open the object builder
                </Button>
            </Stack>
        );
    }

    return (
        <Stack>
            <PageHeader
                title={
                    <Stack
                        component="span"
                        sx={{
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 1.25,
                        }}
                    >
                        <ObjectIcon name={object.icon} size={22} />
                        {object.name}
                    </Stack>
                }
                subtitle={`${pagination.total ?? 0} records`}
                action={
                    <Stack sx={{ flexDirection: "row", gap: 1.5 }}>
                        <Stack
                            sx={{
                                flexDirection: "row",
                                border: "1px solid var(--border-mid)",
                                borderRadius: "3px",
                                overflow: "hidden",
                            }}
                        >
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<TableRowsRounded />}
                                sx={{ height: 42, borderRadius: 0 }}
                            >
                                List
                            </Button>
                            <Button
                                variant="text"
                                startIcon={<ViewKanbanRounded />}
                                sx={{ height: 42, borderRadius: 0 }}
                                onClick={() => navigate(`/o/${slug}/board`)}
                            >
                                Board
                            </Button>
                        </Stack>
                        {/* The pipeline is reachable from the list too — it was
                            previously only on the board, which hid it. */}
                        <Button
                            variant="soft-outlined"
                            startIcon={<AccountTreeRounded />}
                            onClick={() => navigate(`/o/${slug}/pipeline`)}
                        >
                            Pipeline
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddRounded />}
                            onClick={() =>
                                setEditor({
                                    open: true,
                                    record: null,
                                    prefill: null,
                                })
                            }
                        >
                            New {object.name.replace(/s$/i, "")}
                        </Button>
                    </Stack>
                }
            />

            <Stack sx={{ gap: 2 }}>
                <TextField
                    placeholder="Search…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ maxWidth: 320 }}
                />

                <DataTable
                    columns={columns}
                    rows={rows}
                    loading={loading}
                    pagination={pagination}
                    onPageChange={(page) =>
                        load(page, pagination.limit, search)
                    }
                    onLimitChange={(limit) => load(1, limit, search)}
                    onRowClick={(row) =>
                        setEditor({ open: true, record: row, prefill: null })
                    }
                    emptyMessage={`No ${object.name.toLowerCase()} yet`}
                />
            </Stack>

            <RecordEditor
                open={editor.open}
                object={object}
                record={editor.record}
                prefill={editor.prefill}
                users={users}
                pipeline={pipeline}
                onClose={() =>
                    setEditor({ open: false, record: null, prefill: null })
                }
                onSaved={() => {
                    setEditor({ open: false, record: null, prefill: null });
                    load(pagination.page, pagination.limit, search);
                }}
                onRefresh={() =>
                    load(pagination.page, pagination.limit, search)
                }
            />
        </Stack>
    );
}
